import { AnimatePresence, motion } from "framer-motion"
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
} from "react"
import { useResizablePanelWidth } from "@/hooks/use-resizable-panel-width"
import { cn } from "@/lib/utils"

/** Push layout from this width up; below it the drawer is a fixed overlay. Matches Tailwind `lg`. */
const SHELL_DRAWER_PUSH_MQ = "(min-width: 1024px)"

const SHELL_DRAWER_STORAGE_KEY = "shell-drawer-width-px"
const SHELL_DRAWER_DEFAULT_WIDTH = 360
const SHELL_DRAWER_MIN_WIDTH = 360
const SHELL_DRAWER_MAX_WIDTH = 640
const SHELL_DRAWER_MAX_RATIO = 0.4
const SHELL_DRAWER_MIN_SIBLING = 480
/** Overlay width below lg — the drawer does not push, and the drag handle is hidden. */
const SHELL_DRAWER_OVERLAY_WIDTH = "min(360px, 100vw)"

/** Same spring as the agent column. Only open and close use it; drag tracks the pointer. */
const SHELL_DRAWER_SPRING = { type: "spring", bounce: 0, duration: 0.3 } as const
const SHELL_DRAWER_INSTANT = { duration: 0 } as const

const SHELL_DRAWER_INSET_VAR = "--shell-drawer-inset"

const SHELL_DRAWER_SURFACE_CLASS =
  "max-lg:dark:bg-zinc-950 lg:dark:bg-black/20 max-lg:light:bg-ui-bg-elevated lg:light:bg-ui-bg-elevated/80"

// Липкая панель лежит поверх текста. Тот же полупрозрачный слой, что у ящика, дал бы более тёмную полосу.
export const SHELL_DRAWER_COVER_CLASS =
  "max-lg:dark:bg-zinc-950 max-lg:light:bg-ui-bg-elevated lg:dark:bg-[color-mix(in_srgb,black_20%,var(--ui-bg))] lg:light:bg-[color-mix(in_srgb,var(--ui-bg-elevated)_80%,var(--ui-bg))]"

type ShellDrawerProps = {
  open: boolean
  /** Row whose width clamps the panel (`data-desktop-body`). */
  containerRef: RefObject<HTMLElement | null>
  usesToolbarChrome: boolean
  children: ReactNode
}

function useShellDrawerPushLayout(): boolean {
  const [isLg, setIsLg] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(SHELL_DRAWER_PUSH_MQ).matches : false,
  )

  useEffect(() => {
    const mq = window.matchMedia(SHELL_DRAWER_PUSH_MQ)
    const sync = () => setIsLg(mq.matches)
    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])

  return isLg
}

/**
 * Right-hand shell drawer. On lg+ the left edge drags the width (remembered in localStorage);
 * below lg it stays an overlay of fixed width. Writes `--shell-drawer-inset` so fixed docks
 * clear the push width without re-rendering on each pointer move.
 */
export function ShellDrawer({ open, containerRef, usesToolbarChrome, children }: ShellDrawerProps) {
  const isLg = useShellDrawerPushLayout()
  const { width, minWidth, maxWidth, onResizePointerDown, resetWidth } = useResizablePanelWidth({
    storageKey: SHELL_DRAWER_STORAGE_KEY,
    defaultWidth: SHELL_DRAWER_DEFAULT_WIDTH,
    minWidth: SHELL_DRAWER_MIN_WIDTH,
    maxWidth: SHELL_DRAWER_MAX_WIDTH,
    maxWidthRatio: SHELL_DRAWER_MAX_RATIO,
    minSiblingWidth: SHELL_DRAWER_MIN_SIBLING,
    containerRef,
    edge: "trailing",
  })
  const [resizing, setResizing] = useState(false)
  const endResizeRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    return () => endResizeRef.current?.()
  }, [])

  useLayoutEffect(() => {
    const inset = open && isLg ? `${width}px` : "0px"
    document.documentElement.style.setProperty(SHELL_DRAWER_INSET_VAR, inset)
    return () => {
      document.documentElement.style.removeProperty(SHELL_DRAWER_INSET_VAR)
    }
  }, [open, isLg, width])

  const onHandlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    endResizeRef.current?.()
    setResizing(true)
    const end = () => {
      window.removeEventListener("pointerup", end)
      window.removeEventListener("pointercancel", end)
      endResizeRef.current = null
      setResizing(false)
    }
    window.addEventListener("pointerup", end)
    window.addEventListener("pointercancel", end)
    endResizeRef.current = end
    onResizePointerDown(event)
  }

  return (
    <AnimatePresence>
      {open ? (
        <ShellDrawerPanel
          key="shell-drawer"
          width={width}
          isLg={isLg}
          resizing={resizing}
          minWidth={minWidth}
          maxWidth={maxWidth}
          usesToolbarChrome={usesToolbarChrome}
          onResizePointerDown={onHandlePointerDown}
          onResetWidth={resetWidth}
        >
          {children}
        </ShellDrawerPanel>
      ) : null}
    </AnimatePresence>
  )
}

type ShellDrawerPanelProps = {
  width: number
  isLg: boolean
  resizing: boolean
  minWidth: number
  maxWidth: number
  usesToolbarChrome: boolean
  onResizePointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void
  onResetWidth: () => void
  children: ReactNode
}

function ShellDrawerPanel({
  width,
  isLg,
  resizing,
  minWidth,
  maxWidth,
  usesToolbarChrome,
  onResizePointerDown,
  onResetWidth,
  children,
}: ShellDrawerPanelProps) {
  // Mounts with the panel, so each open starts on the spring and later width changes do not.
  const [opening, setOpening] = useState(true)
  useEffect(() => {
    const id = window.setTimeout(() => setOpening(false), SHELL_DRAWER_SPRING.duration * 1000)
    return () => window.clearTimeout(id)
  }, [])

  const animateWidth = isLg ? width : SHELL_DRAWER_OVERLAY_WIDTH

  return (
    <motion.aside
      data-shell-drawer
      initial={{ width: 0, opacity: 0 }}
      animate={{ width: animateWidth, opacity: 1 }}
      exit={{ width: 0, opacity: 0, transition: SHELL_DRAWER_SPRING }}
      transition={opening && !resizing ? SHELL_DRAWER_SPRING : SHELL_DRAWER_INSTANT}
      className={cn(
        // overflow-x only: keep the width-anim clip; allow the Agents tab badge above the header
        "overflow-x-hidden border-l",
        // Below lg: overlay under the toolbar (z in ui-theme.css). lg+: in-flow push.
        "max-lg:fixed max-lg:top-[var(--shell-window-bar-height)] max-lg:right-0 max-lg:bottom-0",
        "lg:sticky lg:shrink-0",
        usesToolbarChrome ? "lg:top-0 lg:h-full" : "lg:top-14 lg:h-[calc(100vh-3.5rem)]",
        "border-ui-border",
        SHELL_DRAWER_SURFACE_CLASS,
      )}
    >
      {isLg ? (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={Math.round(width)}
          aria-valuemin={minWidth}
          aria-valuemax={Math.round(maxWidth)}
          onPointerDown={onResizePointerDown}
          onDoubleClick={onResetWidth}
          className="group/resize absolute inset-y-0 left-0 z-20 w-2 cursor-ew-resize touch-none"
        >
          <span
            aria-hidden
            className="absolute inset-y-0 left-0 w-px transition-colors group-hover/resize:bg-ui-accent group-active/resize:bg-ui-accent"
          />
        </div>
      ) : null}
      <div
        className={cn(
          "flex flex-col max-lg:h-full max-lg:w-[min(360px,100vw)]",
          usesToolbarChrome ? "lg:h-full" : "lg:h-[calc(100vh-3.5rem)]",
        )}
        style={isLg ? { width } : undefined}
      >
        {children}
      </div>
    </motion.aside>
  )
}
